import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { shakeAnimation } from '@freelancer/animations';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { DirectivesModule } from '@freelancer/directives';
import {
  AutoGrowDirective,
  ControlValueAccessorDirective,
} from '@freelancer/directives';
import { ButtonComponent } from '@freelancer/ui/button';
import { IconModule } from '@freelancer/ui/icon';
import { textVariants } from '@freelancer/ui/tailwind-text';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { ValidationErrorComponent } from '@freelancer/ui/validation-error';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import { Subscription } from 'rxjs';
import { Focus } from '../focus';
import { generateUniqueID } from '../helpers/helpers';
import { IconName, IconSize } from '../icon';
import { AutoFocusOption } from '../input';
import type { TextareaSelectEvent, TextareaSelection } from './textarea.types';
import {
  Resize,
  TextAreaClearContentButtonPosition,
  TextareaMaxCharPosition,
} from './textarea.types';
import { PlaceholderColor } from '../text/text.types';

@Component({
  standalone: true,
  selector: 'fl-textarea',
  imports: [
    ButtonComponent,
    DirectivesModule,
    IconModule,
    ReactiveFormsModule,
    ValidationErrorComponent,
  ],
  template: `
    <textarea
      #nativeElement
      class="TextArea {{
        control.invalid && (control.touched || control.dirty)
          ? 'mb-xxxsmall'
          : ''
      }}"
      flControlValueAccessor
      [attr.aria-errormessage]="uniqueId"
      [attr.aria-invalid]="hasError && !isWarning"
      [attr.aria-label]="label"
      [attr.data-auto-grow]="autoGrow"
      [attr.data-font-color]="fontColor"
      [attr.data-placeholder-no-wrap]="placeholderNoWrap"
      [attr.data-size]="size"
      [attr.data-size-tablet]="sizeTablet"
      [attr.data-size-desktop]="sizeDesktop"
      [attr.data-size-desktop-xlarge]="sizeDesktopXLarge"
      [attr.data-weight]="weight"
      [attr.data-weight-tablet]="weightTablet"
      [attr.data-weight-desktop]="weightDesktop"
      [attr.id]="id"
      [attr.data-borderless]="borderless"
      [attr.data-borderless-focus-line]="borderlessFocusLine"
      [attr.placeholder]="placeholder"
      [attr.data-resize]="resize"
      [attr.maxlength]="maxLength ? maxLength : null"
      [attr.data-hide-scrollbar]="hideScrollbar"
      [attr.data-as-input]="asInput"
      [attr.data-placeholder-color]="placeholderColor"
      [formControl]="control"
      [class.IsInvalid]="hasError && !isWarning"
      [attr.rows]="rows"
      [attr.autocomplete]="autoComplete"
      [attr.disabled]="attrDisabled"
      [flAutoGrow]="autoGrow ? control : undefined"
      [flAutoGrowMaxHeight]="autoGrowMaxHeight"
      (focus)="handleFocus()"
      (blur)="handleBlur()"
      (keyup)="handleKeyUp()"
      (input)="handleInput()"
      [@shakeAnimation]="{
        value: shakeState,
        params: {
          duration: 200,
          translateX: '3px',
        },
      }"
      (@shakeAnimation.done)="shakeDone()"
      (select)="handleSelect()"
    ></textarea>
    <div
      class="Footer"
      [class.Footer-reverse]="
        enableClearButton && maxCharacter === undefined
          ? clearButtonPosition === 'right'
          : maxCharacter !== undefined &&
            maxCharacterPosition === TextareaMaxCharPosition.RIGHT
      "
    >
      @if (hasError) {
        <fl-validation-error
          class="Footer-leftSide"
          [id]="uniqueId"
          [control]="control"
          [isWarning]="isWarning"
        ></fl-validation-error>
      }
      @if (
        maxCharacter !== undefined &&
        (hasError
          ? maxCharacterPosition === TextareaMaxCharPosition.RIGHT
          : true)
      ) {
        <p
          class="Counter text-xxsmall {{
            remainingCharCount >= 0 ? 'text-foreground' : 'text-error'
          }}"
          [class.IsFocus]="!autoHideCounter || textareaFocused === true"
          [class.Footer-leftSide]="
            maxCharacterPosition === TextareaMaxCharPosition.LEFT
          "
          [class.Footer-rightSide]="
            maxCharacterPosition === TextareaMaxCharPosition.RIGHT
          "
          [class.hide]="
            maxCharacterPosition === TextareaMaxCharPosition.LEFT && hasError
          "
        >
          @if (displayCurrentCharCount) {
            <ng-container i18n="Textarea field current and max characters text">
              {{ currentCharCount }}/{{ maxCharacter }} characters
            </ng-container>
          } @else {
            @if (remainingCharCount >= 0) {
              <ng-container i18n="Textarea field characters left text">
                {{ remainingCharCount }} characters left
              </ng-container>
            }
            @if (remainingCharCount < 0) {
              <ng-container i18n="Textarea field characters over limit text">
                {{ -remainingCharCount }} characters over limit
              </ng-container>
            }
          }
        </p>
      }

      @if (
        enableClearButton && (clearButtonPosition === 'left' ? !hasError : true)
      ) {
        <fl-button
          class="ClearContentButton"
          [class.Footer-leftSide]="clearButtonPosition === 'left'"
          [class.Footer-rightSide]="clearButtonPosition === 'right'"
          [display]="'flex'"
          (click)="clearContent()"
        >
          <fl-icon
            class="ClearContentButton-icon mr-xxxsmall"
            [fillIconFont]="false"
            [name]="clearButtonIcon"
            [size]="clearButtonIconSize"
            [label]="clearButtonIconLabel"
            [useIconFont]="true"
          ></fl-icon>
          @if (clearButtonText) {
            <p [class]="clearButtonTextCls">
              {{ clearButtonText }}
            </p>
          }
        </fl-button>
      }
    </div>
  `,
  styleUrls: ['./textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [shakeAnimation],
})
export class TextareaComponent
  implements AfterViewInit, OnChanges, OnInit, OnDestroy
{
  private cd = inject(ChangeDetectorRef);
  private focus = inject(Focus);

  TextareaMaxCharPosition = TextareaMaxCharPosition;

  @RepetitiveSubscription()
  private errorSubscription?: Subscription;

  attrDisabled?: boolean;
  currentCharCount = 0;
  textareaFocused = false;
  isWarning = false;
  remainingCharCount = 0;
  shakeState = 'void';
  uniqueId: string;

  @Input() id: string;
  @Input() control: FormControl<string>;
  @Input() borderless = false;
  @Input() borderlessFocusLine = true;
  @Input() placeholder = '';
  @Input() rows = 2;
  @Input() fontColor: FontColor.FOREGROUND | FontColor.LIGHT =
    FontColor.FOREGROUND;
  /**
   * Sets the color of the placeholder text
   */
  @Input() placeholderColor: PlaceholderColor.INHERIT | PlaceholderColor.LIGHT =
    PlaceholderColor.INHERIT;
  /**
   * Sets aria label for accessibility when there is no associated visible text
   */
  @Input() label?: string;
  @Input() maxCharacter?: number;
  @Input() maxLength?: number;
  @Input() autoComplete = 'off';
  @Input() resize: Resize = Resize.VERTICAL;
  @Input() maxCharacterPosition?: TextareaMaxCharPosition =
    TextareaMaxCharPosition.LEFT;
  @Input() size: TextSize = TextSize.XSMALL;
  @Input() sizeTablet?: TextSize;
  @Input() sizeDesktop?: TextSize;
  @Input() sizeDesktopXLarge?: TextSize;
  @Input() weight = FontWeight.NORMAL;
  @Input() weightTablet?: FontWeight;
  @Input() weightDesktop?: FontWeight;

  /** Hides max character counter when blurred out of field */
  @Input() autoHideCounter?: boolean = true;

  /** Displays current/max characters when maxCharacter is provided */
  @Input() displayCurrentCharCount?: boolean = false;

  /** First validation happens only on blur/submit, and validates on keyup once an error exists */
  @Input() dynamicValidation?: 'blur' | 'submit';

  @Input() set disabled(value: boolean) {
    this.attrDisabled = value ? true : undefined;
  }

  @Input() autoFocus: AutoFocusOption = AutoFocusOption.NONE;

  /** Set to `true` to automatically adjust the height of the text area when typing */
  @Input() autoGrow = false;

  /**
   * Maximum height in pixels the text area will grow.
   * `autoGrow` must be true to have an effect.
   *
   * Minimum height is always the default height based on `rows`.
   */
  @Input() autoGrowMaxHeight = 72;

  @Input() enableClearButton = false;
  /**
   * Position in the footer of the clear button.
   * Input does nothing if `maxCharacter` is enabled, it will always go to the opposite side of `maxCharacter`
   */
  @Input() clearButtonPosition: TextAreaClearContentButtonPosition = 'right';
  @Input() clearButtonIcon: IconName = 'ui-bin';
  @Input() clearButtonIconSize: IconSize = IconSize.SMALL;
  @Input() clearButtonText?: string;
  @Input() clearButtonTextSize: TextSize = TextSize.SMALL;
  @Input() clearButtonIconLabel?: string;
  @Input() hideScrollbar = false;
  // Used by ai-textarea to render the textarea as an input
  @Input() asInput = false;
  /**
   * When false, suppresses in-component validation error messages and shaking animations.
   */
  @Input() showValidationError = true;
  /** Disable placeholder wrapping */
  @Input() placeholderNoWrap = false;

  @ViewChild(ControlValueAccessorDirective)
  controlValueAccessorDirective: ControlValueAccessorDirective;
  @ViewChild('nativeElement') nativeElement: ElementRef<HTMLTextAreaElement>;

  @ViewChild(AutoGrowDirective) textareaAutogrow: AutoGrowDirective;

  @Output() inputBlur = new EventEmitter<void>();
  @Output() inputFocus = new EventEmitter<void>();
  /** Event emitted everytime a user highlights a text in the textarea */
  @Output() inputSelect = new EventEmitter<TextareaSelectEvent>();
  @Output() inputHandled = new EventEmitter<void>();

  clearButtonTextCls: string;

  get hasError(): boolean {
    return (
      this.showValidationError &&
      this.control.invalid &&
      (this.control.touched || this.control.dirty)
    );
  }

  ngOnInit(): void {
    this.computeClasses();
  }

  ngAfterViewInit(): void {
    if (this.autoFocus !== AutoFocusOption.NONE) {
      this.focus.focusElement(this.nativeElement, {
        allowMobile: this.autoFocus === AutoFocusOption.ALWAYS,
      });
    }

    this.updateCharCount();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('label' in changes || !this.uniqueId) {
      this.uniqueId = `textarea-${this.label}-errorId-${generateUniqueID()}`;
    }

    if ('control' in changes || 'dynamicValidation' in changes) {
      if (this.errorSubscription) {
        this.errorSubscription.unsubscribe();
      }
      this.errorSubscription = this.control.statusChanges.subscribe(() => {
        if (this.hasError && !this.isWarning && this.dynamicValidation) {
          this.shakeState = 'active';
        }
        this.cd.markForCheck();
      });

      // This sets the control's updateOn value to `blur`/`submit` if dynamicValidation is set
      if (this.control && this.dynamicValidation) {
        this.control = Object.defineProperty(this.control, 'updateOn', {
          get: () => this.dynamicValidation,
          configurable: true,
        });
      }

      if ('clearButtonTextSize' in changes) {
        this.computeClasses();
      }
    }

    // Compute position of the clear button if enabled
    if (
      this.enableClearButton &&
      ('maxCharacter' in changes ||
        'maxCharacterPosition' in changes ||
        'clearButtonPosition' in changes)
    ) {
      this.clearButtonPosition =
        // Go to the opposite position of the max character
        this.maxCharacter !== undefined
          ? this.maxCharacterPosition === TextareaMaxCharPosition.LEFT
            ? 'right'
            : 'left'
          : this.clearButtonPosition;
    }
  }

  ngOnDestroy(): void {
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
  }

  handleBlur(): void {
    this.textareaFocused = false;

    this.isWarning = false;
    if (this.dynamicValidation === 'blur') {
      this.validateField();
    }

    if (this.hasError) {
      this.shakeState = 'active';
    }

    this.inputBlur.emit();
  }

  handleKeyUp(): void {
    this.updateCharCount();
    this.handleDynamicValidation();
  }

  handleDynamicValidation(): void {
    // We want to only keep validating if there is already an existing error
    if (!this.dynamicValidation || !this.hasError) {
      return;
    }

    this.validateField();
  }

  handleFocus(): void {
    this.textareaFocused = true;

    if (this.dynamicValidation && this.hasError) {
      this.isWarning = true;
    }

    this.inputFocus.emit();
  }

  // We have to manually update the max character counter instead of getting
  // control.value.length because updateOn is 'blur'/'submit' when dynamic validation is used
  updateCharCount(): void {
    if (this.maxCharacter) {
      this.remainingCharCount =
        this.maxCharacter - this.nativeElement.nativeElement.value.length;

      this.currentCharCount = this.nativeElement.nativeElement.value.length;
    }
  }

  insertAtCursor(
    textToInsert: string,
    options: { paddedWithSpaces: boolean } = { paddedWithSpaces: false },
  ): void {
    const currentValue = this.control.value;

    if (!currentValue) {
      this.control.setValue(textToInsert);
      // Set the cursor position to the end of the inserted text
      this.controlValueAccessorDirective.setCursorPosition(textToInsert.length);
    } else {
      const { selectionStart, selectionEnd } =
        this.controlValueAccessorDirective.getCursorSelectionPosition();

      // If the cursor is not at the start of a message (e.g. AB|CD, not |ABCD),
      // check if the character before the cursor position is not a space and
      // if padded option is enabled then add a space before the text
      const prependToInsertedText =
        options.paddedWithSpaces &&
        selectionStart !== 0 &&
        !/\s/.test(currentValue[selectionStart - 1])
          ? ' '
          : '';

      // If the cursor is not at the end of a message (e.g. AB|CD, not ABCD|),
      // check if the character after the cursor position is not a space and
      // if padded option is enabled then add a space after the text
      const appendToInsertedText =
        options.paddedWithSpaces &&
        selectionEnd !== currentValue.length &&
        !/\s/.test(currentValue[selectionEnd])
          ? ' '
          : '';

      // The emoji to insert with/without a space before and after it
      const newTextValue = `${prependToInsertedText}${textToInsert}${appendToInsertedText}`;

      this.control.setValue(
        `${currentValue.substring(
          0,
          selectionStart,
        )}${newTextValue}${currentValue.substring(selectionEnd)}`,
      );

      const cursorNewPosition = selectionStart + newTextValue.length;
      this.controlValueAccessorDirective.setCursorPosition(cursorNewPosition);
    }
  }

  focusIn(): void {
    this.focus.focusElement(this.nativeElement, { allowMobile: true });
  }

  validateField(): void {
    this.control.setValue(this.nativeElement.nativeElement.value);

    if (this.nativeElement.nativeElement.value !== '') {
      dirtyAndValidate(this.control);
    } else {
      this.control.reset('');
    }
  }

  shakeDone(): void {
    this.shakeState = 'void';
  }

  handleInput(): void {
    // Update auto-grow when typing if dynamicValidation is set to 'blur'
    // Since dynamicValidation on blur won't detect value change when typing
    if (this.autoGrow && this.dynamicValidation === 'blur') {
      this.textareaAutogrow.adjust();
    }

    this.inputHandled.emit();
  }

  clearContent(): void {
    this.control.reset();
  }

  handleSelect(): void {
    const selection = this.getSelection();
    if (selection) {
      this.inputSelect.emit({
        selection,
      });
    }
  }

  getSelection(): TextareaSelection | undefined {
    if (!this.nativeElement?.nativeElement) return;

    const { selectionStart, selectionEnd } = this.nativeElement.nativeElement;
    return {
      selectionStart,
      selectionEnd,
      selectionContent: this.control.value.substring(
        selectionStart,
        selectionEnd,
      ),
    };
  }

  private computeClasses(): void {
    this.clearButtonTextCls = textVariants({
      class: 'ClearContentButton-text',
      size: this.clearButtonTextSize,
      weight: FontWeight.NORMAL,
    });
  }
}
